import React from "react";
import { useTranslation } from "react-i18next";
import { BedDouble, HandPlatter, Sparkles, Star } from "lucide-react";
import { REVENUE_CHART_CARD_ICON_STROKE_WIDTH } from "../revenue/constants";
import { SERVICES_CHART_CARD_ICON_COLOR } from "../services/constants";
import {
  REVIEWS_CATEGORY_BAR_FILL,
  REVIEWS_CATEGORY_BAR_FILL_OPACITY,
  REVIEWS_CATEGORY_BAR_HEIGHT,
  REVIEWS_CATEGORY_BAR_RADIUS,
  REVIEWS_CATEGORY_BAR_TRACK_BG,
  REVIEWS_CATEGORY_CARD_BORDER,
  REVIEWS_CATEGORY_CARD_HEIGHT,
  REVIEWS_CATEGORY_CARD_PADDING,
  REVIEWS_CATEGORY_CARD_RADIUS,
  REVIEWS_CATEGORY_ROW_GAP,
} from "./constants";
import type { CategoryKey, CategoryRatingRow } from "./types";

const CATEGORY_ICONS: Record<CategoryKey, React.ElementType> = {
  purity: Sparkles,
  comfort: BedDouble,
  service: HandPlatter,
};

function StarDistributionRow({
  stars,
  percent,
}: {
  stars: number;
  percent: number;
}) {
  return (
    <div
      className="flex w-full items-center"
      style={{
        gap: REVIEWS_CATEGORY_ROW_GAP,
        minHeight: REVIEWS_CATEGORY_BAR_HEIGHT,
      }}
    >
      <div className="flex shrink-0 items-center gap-1">
        <span className="text-base font-medium leading-none text-[#171717]">
          {stars}
        </span>
        <Star
          className="size-6 shrink-0 fill-[#FACC15] text-[#FACC15]"
          strokeWidth={0}
          aria-hidden
        />
      </div>
      <div
        className="relative min-w-0 flex-1 overflow-hidden"
        style={{
          height: REVIEWS_CATEGORY_BAR_HEIGHT,
          borderRadius: REVIEWS_CATEGORY_BAR_RADIUS,
          backgroundColor: REVIEWS_CATEGORY_BAR_TRACK_BG,
        }}
        role="presentation"
      >
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${percent}%`,
            borderRadius: REVIEWS_CATEGORY_BAR_RADIUS,
            backgroundColor: REVIEWS_CATEGORY_BAR_FILL,
            opacity: REVIEWS_CATEGORY_BAR_FILL_OPACITY,
          }}
        />
      </div>
      <span className="ml-auto shrink-0 text-sm font-normal leading-none text-[#171717]">
        {percent}%
      </span>
    </div>
  );
}

interface ReviewsCategoryBreakdownCardProps {
  category: CategoryKey;
  rows: CategoryRatingRow[];
}

export const ReviewsCategoryBreakdownCard: React.FC<
  ReviewsCategoryBreakdownCardProps
> = ({ category, rows }) => {
  const { t } = useTranslation();
  const Icon = CATEGORY_ICONS[category];
  const sortedRows = [...rows].sort((a, b) => b.stars - a.stars);

  return (
    <article
      className="flex min-w-0 flex-1 flex-col justify-between bg-white font-sans box-border"
      style={{
        height: REVIEWS_CATEGORY_CARD_HEIGHT,
        minWidth: 0,
        padding: REVIEWS_CATEGORY_CARD_PADDING,
        borderRadius: REVIEWS_CATEGORY_CARD_RADIUS,
        border: `1px solid ${REVIEWS_CATEGORY_CARD_BORDER}`,
      }}
    >
      <div className="mb-[25px] flex items-center gap-3">
        <Icon
          className="size-4 shrink-0"
          style={{ color: SERVICES_CHART_CARD_ICON_COLOR }}
          strokeWidth={REVENUE_CHART_CARD_ICON_STROKE_WIDTH}
          aria-hidden
        />
        <h3 className="text-sm font-semibold leading-normal text-[#171717]">
          {t(`analytics.reviews.categories.${category}`, category)}
        </h3>
      </div>

      <div className="flex min-h-0 flex-1 flex-col justify-between">
        {sortedRows.map((row) => (
          <StarDistributionRow
            key={row.stars}
            stars={row.stars}
            percent={row.percent}
          />
        ))}
      </div>
    </article>
  );
};
